import { useCorpusFieldValues } from "@noa/api-client";
import type { CorpusOverview } from "@noa/api-core";
import { Search, X } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Select } from "~/components/ui";
import {
  type CorpusFilterForm,
  countedOptions,
  EMPTY_FILTER_FORM,
  isFormEmpty,
  sourceLabelKey,
} from "~/utils/corpus-admin";

interface CorpusFilterBarProps {
  form: CorpusFilterForm;
  onChange: (form: CorpusFilterForm) => void;
  overview: CorpusOverview | undefined;
  /** The filterable text fields of the chosen type's schema. */
  fields: string[];
}

/** A filter with its name shown above it: a placeholder alone disappears once a value is picked. */
function Labeled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-noa-muted">
      {label}
      {children}
    </label>
  );
}

interface FieldSelectProps {
  documentType: string;
  field: string;
  value: string | undefined;
  onChange: (value: string) => void;
}

/**
 * One schema field's values, counted within the chosen type. A field with a single value
 * for the whole type narrows nothing, so it is left out unless already chosen.
 */
function FieldSelect({ documentType, field, value, onChange }: FieldSelectProps) {
  const { i18n } = useTranslation("admin");
  const { values, isLoading } = useCorpusFieldValues(documentType, field);

  if (!value && (isLoading || values.length < 2)) return null;

  return (
    <Labeled label={field}>
      <Select
        aria-label={field}
        value={value || null}
        options={countedOptions(values, (v) => v, i18n.language)}
        onChange={(next) => onChange(next ?? "")}
        allowClear
        showSearch
        loading={isLoading}
        className="w-44"
      />
    </Labeled>
  );
}

/** Type, source, schema fields, name and ingestion dates; every filter set must match. */
export function CorpusFilterBar({ form, onChange, overview, fields }: CorpusFilterBarProps) {
  const { t, i18n } = useTranslation("admin");
  const update = (patch: Partial<CorpusFilterForm>) => onChange({ ...form, ...patch });

  return (
    <div className="mb-4 flex flex-wrap items-end gap-3">
      <Labeled label={t("vectorstoreAdmin.filters.type")}>
        <Select
          aria-label={t("vectorstoreAdmin.filters.type")}
          value={form.documentType ?? null}
          options={countedOptions(overview?.documents_by_type ?? [], (v) => v, i18n.language)}
          // A new type has its own schema fields: the previous type's values no longer apply
          onChange={(next) => update({ documentType: next ?? undefined, fields: {} })}
          allowClear
          showSearch
          className="w-64"
        />
      </Labeled>
      <Labeled label={t("vectorstoreAdmin.filters.source")}>
        <Select
          aria-label={t("vectorstoreAdmin.filters.source")}
          value={form.sourceSystem ?? null}
          options={countedOptions(
            overview?.documents_by_source ?? [],
            (v) => t(sourceLabelKey(v), { defaultValue: v }),
            i18n.language
          )}
          onChange={(next) => update({ sourceSystem: next ?? undefined })}
          allowClear
          className="w-48"
        />
      </Labeled>
      {form.documentType &&
        fields.map((field) => (
          <FieldSelect
            key={field}
            documentType={form.documentType!}
            field={field}
            value={form.fields[field]}
            onChange={(value) => update({ fields: { ...form.fields, [field]: value } })}
          />
        ))}
      <Labeled label={t("vectorstoreAdmin.filters.name")}>
        <Input
          aria-label={t("vectorstoreAdmin.filters.name")}
          prefix={<Search size={14} />}
          value={form.name}
          onChange={(event) => update({ name: event.target.value })}
          allowClear
          className="w-64"
        />
      </Labeled>
      <Labeled label={t("vectorstoreAdmin.filters.from")}>
        <Input
          type="date"
          aria-label={t("vectorstoreAdmin.filters.from")}
          value={form.ingestedFrom}
          max={form.ingestedTo || undefined}
          onChange={(event) => update({ ingestedFrom: event.target.value })}
          className="w-40"
        />
      </Labeled>
      <Labeled label={t("vectorstoreAdmin.filters.to")}>
        <Input
          type="date"
          aria-label={t("vectorstoreAdmin.filters.to")}
          value={form.ingestedTo}
          min={form.ingestedFrom || undefined}
          onChange={(event) => update({ ingestedTo: event.target.value })}
          className="w-40"
        />
      </Labeled>
      {!isFormEmpty(form) && (
        <Button variant="text" icon={<X size={14} />} onClick={() => onChange(EMPTY_FILTER_FORM)}>
          {t("vectorstoreAdmin.filters.reset")}
        </Button>
      )}
    </div>
  );
}
